/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {
    ArrowDownTrayIcon,
    ArrowRightIcon,
    ArrowUpTrayIcon,
    CheckIcon,
    ChevronRightIcon,
    ClockIcon,
    CreditCardIcon,
    InformationCircleIcon,
    MinusIcon,
    PaperAirplaneIcon,
    PlusIcon,
    TicketIcon,
    UsersIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import {Tooltip} from "react-tooltip";
import {useTranslation} from "react-i18next";
import {ChevronDownIcon, ChevronUpIcon, ExclamationTriangleIcon, TrashIcon} from "@heroicons/react/24/solid";

interface ButtonSelectPackageProps {
    onClick: () => void;
    label: string;
    icon: string;
    disabled?: boolean;
    className?: string;
    buttonType?: string;
    id?: string;
    type?: string;
    callToAction?: boolean;
    children?: React.ReactNode | React.ReactNode[];
    isLoading?: boolean;
    showWarning?: boolean;
}

const ButtonSubmit = (props: ButtonSelectPackageProps) => {
    const buttonType = props.buttonType ? props.buttonType : "cta";
    const {t} = useTranslation();
    const chooseHelper = () => {
        if (buttonType === "secondary-cta" && props.disabled) {
            return t("You have selected another option from this group");
        }
        if (props.disabled) {
            return t("This option is disabled");
        }
    };
    return (
        <button
            className={clsx(
                props?.className && props.className,
                buttonType === "arbo-orange" && "CTA bg-arbo-orange border-arbo-orange border text-black",
                buttonType === "cta" && "CTA bg-cta-800 border-cta-800 border  text-white ",
                buttonType === "secondary-cta" && "SECONDARY_CTA bg-cta-800 border-cta-800 border  text-white ",
                buttonType === "selected" && "SELECTED bg-primary-800 border-primary-800 text-white border",
                buttonType === "selectedBlack" && "selectedBlack bg-black border-black text-white border",
                buttonType === "red" && "red bg-red-500 border-red-500 text-white border",
                "ButtonSubmit hover:no-underline flex flex-row items-center text-center justify-between p-2 py-3 sm:p-3 text-xl font-medium relative overflow-hidden active:bg-opacity-80 focus:bg-opacity-80 hover:bg-opacity-90 disabled:bg-gray-500 disabled:border-gray-500 cursor-pointer",
                props.disabled && "opacity-50"
            )}
            data-for={"chooseHelp"}
            data-tip={chooseHelper()}
            disabled={props.disabled}
            id={props.id ? props.id : "ButtonSubmit"}
            type={"submit"}
            onClick={props.onClick}
        >
            {props.isLoading && (
                <svg className="animate-spin flex-none w-5 h-5 mr-1.5" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                        className="opacity-75"
                        d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        fill="currentColor"
                    />
                </svg>
            )}
            {!props?.isLoading && (
                <div className={"z-10"}>
                    {props.icon === "save" && <ArrowDownTrayIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "send" && <ArrowUpTrayIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "right" && <ArrowRightIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "note" && <PaperAirplaneIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "check" && <CheckIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "clock" && <ClockIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "ticket" && <TicketIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "card" && <CreditCardIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "persons" && <UsersIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "info" && <InformationCircleIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "plus" && <PlusIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "minus" && <MinusIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "plusChar" && <span className={"font-bold text-2xl leading-none mr-2"}>+</span>}
                    {props.icon === "trash" && <TrashIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "down" && <ChevronDownIcon className={"w-6 h-6 mr-2"} />}
                    {props.icon === "up" && <ChevronUpIcon className={"w-6 h-6 mr-2"} />}
                </div>
            )}
            <span className="pointer-events-none select-none grow flex items-center justify-center text-center z-10">{props.label}</span>
            {props?.showWarning && (
                <span className={"bg-red-500 px-0.5 py-0.5 rounded border border-white/40 shadow-sm shadow-black/20 drop-shadow"}>
                    <ExclamationTriangleIcon className={"size-5 flex-none text-white"} />
                </span>
            )}
            {props.children}
            {props.icon === "chevron-right" && <ChevronRightIcon className={"w-6 h-6 ml-2"} />}
            <Tooltip data-tooltip-float={true} id={"chooseHelp"} />
        </button>
    );
};

export default ButtonSubmit;
